import { Injectable, signal, effect } from '@angular/core';

export type Theme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<Theme>(ThemeService.readStoredTheme());

  constructor() {
    // Aplica de inmediato el valor ya leído en la signal (el script de index.html evita el
    // FOUC; esto alinea signal, DOM y localStorage para que el effect no revierta a 'light').
    this.applyTheme(this.theme());

    // Efecto reactivo para mantener sincronía continua
    effect(() => {
      const theme = this.theme();
      this.applyTheme(theme);
    });
  }

  toggleTheme(): void {
    const newTheme = this.theme() === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
    // Asegura que el DOM lo aplique de inmediato
    this.applyTheme(theme);
  }

  /** Aplica los cambios al DOM y sincroniza localStorage */
  private applyTheme(theme: Theme): void {
    const root = document.documentElement;
    // Suprime transiciones durante el flip de tema: evita que los colores (bordes del
    // header/sidebar, fondos) "arrastren" su valor anterior animándose 300 ms. Se reactivan
    // tras dos frames, cuando el navegador ya pintó el nuevo tema sin animación.
    root.classList.add('theme-switching');
    root.classList.toggle('dark', theme === 'dark');
    document.body.classList.toggle('dark:bg-gray-900', theme === 'dark');
    localStorage.setItem('theme', theme);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove('theme-switching')),
    );
  }

  private static readStoredTheme(): Theme {
    try {
      const saved = localStorage.getItem('theme');
      return saved === 'dark' || saved === 'light' ? saved : 'light';
    } catch {
      return 'light';
    }
  }
}
